import React, { useState, useEffect } from 'react';
import { COLORS, sharedStyles, callClaude } from '../shared';

export default function DisputeStrategy({ report, onComplete }) {
  const [strategy, setStrategy]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [selected, setSelected]   = useState([]);

  useEffect(() => { if (report && !strategy) generateStrategy(); }, [report]);

  const generateStrategy = async () => {
    setLoading(true); setError(null);
    try {
      const system = `You are a credit strategist at Coleman Financial Firm. Return ONLY compact JSON, no markdown, no backticks, no extra spaces.`;
      const prompt = `Analyze this credit report and return a dispute strategy as compact JSON.

Client: ${report.clientName || 'Client'}
Scores: TU ${report.scores?.TransUnion || 'N/A'}, EX ${report.scores?.Experian || 'N/A'}, EQ ${report.scores?.Equifax || 'N/A'}
Accounts: ${JSON.stringify(report.accounts?.slice(0,10) || [])}
Inquiries: ${JSON.stringify(report.inquiries?.slice(0,5) || [])}
Public Records: ${JSON.stringify(report.publicRecords?.slice(0,3) || [])}
Summary: ${JSON.stringify(report.summary || {})}

Return ONLY this JSON (keep values short, max 5 priorityActions):
{"clientName":"","overallScore":{"assessment":"","summary":""},"priorityActions":[{"id":"1","priority":"HIGH","category":"","creditor":"","bureau":"","issue":"","action":"","letterType":"dispute","estimatedImpact":"","timeframe":"30-45 days","fcraGrounds":""}],"roundPlan":{"round1":{"focus":"","actions":[""],"expectedOutcome":""},"round2":{"focus":"","actions":[""],"expectedOutcome":""},"round3":{"focus":"","actions":[""],"expectedOutcome":""}},"quickWins":[""],"scoreProjection":{"current":"","projected90Days":"","projected6Months":""},"warnings":[""]}`;

      const text   = await callClaude([{ role:'user', content:prompt }], system);
      const clean  = text.replace(/```json|```/g,'').trim();
      const parsed = JSON.parse(clean);
      setStrategy(parsed);
    } catch(err) {
      setError('Error: ' + (err.message || 'Unable to generate strategy. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const [emailSent, setEmailSent]   = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [clientEmail, setClientEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);

  const handleExport = async () => {
    if (!clientEmail.trim()) { setShowEmailInput(true); return; }
    setEmailLoading(true); setEmailError(null);
    try {
      const lines = [];
      lines.push('COLEMAN FINANCIAL FIRM — DISPUTE STRATEGY REPORT');
      lines.push('='.repeat(50));
      lines.push(`Client: ${strategy.clientName || 'N/A'}`);
      lines.push(`Date Generated: ${new Date().toLocaleDateString()}`);
      lines.push('');
      lines.push('CREDIT SCORES');
      lines.push(`TransUnion: ${report?.scores?.TransUnion || 'N/A'} | Experian: ${report?.scores?.Experian || 'N/A'} | Equifax: ${report?.scores?.Equifax || 'N/A'}`);
      lines.push('');
      lines.push('OVERALL ASSESSMENT');
      lines.push(strategy.overallScore?.summary || 'N/A');
      lines.push('');
      lines.push('PRIORITY ACTIONS');
      strategy.priorityActions?.forEach((a, i) => {
        lines.push(`${i+1}. [${a.priority}] ${a.creditor} — ${a.issue}`);
        lines.push(`   Action: ${a.action}`);
        lines.push(`   Letter: ${a.letterType} | Impact: ${a.estimatedImpact} | Timeline: ${a.timeframe}`);
        lines.push('');
      });
      lines.push('ROUND PLAN');
      ['round1','round2','round3'].forEach((r, i) => {
        const rd = strategy.roundPlan?.[r];
        if (!rd) return;
        lines.push(`Round ${i+1}: ${rd.focus}`);
        lines.push(`Expected: ${rd.expectedOutcome}`);
        lines.push('');
      });
      lines.push('SCORE PROJECTION');
      lines.push(`Current: ${strategy.scoreProjection?.current || 'N/A'} | 90 Days: ${strategy.scoreProjection?.projected90Days || 'N/A'} | 6 Months: ${strategy.scoreProjection?.projected6Months || 'N/A'}`);
      lines.push('');
      lines.push('='.repeat(50));
      lines.push('Coleman Financial Firm — Building Wealth. Changing Lives.');
      lines.push('colemanfinancialfirm.com | support@colemanfinancialfirm.com');

      await window.emailjs.send(
        'service_yzyxxyh',
        'template_td6pasf',
        {
          to_email:         clientEmail.trim(),
          client_name:      strategy.clientName || 'Valued Client',
          strategy_content: lines.join('\n'),
        },
        'RHyRm2IuaYsqks2yw'
      );
      setEmailSent(true);
      setShowEmailInput(false);
    } catch(err) {
      setEmailError('Failed to send email. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleProceed = () => {
    const selectedActions = strategy.priorityActions.filter(a => selected.includes(a.id));
    onComplete({ strategy, selectedActions });
  };

  const priorityColor = (p) => p==='HIGH' ? '#c0392b' : p==='MEDIUM' ? COLORS.gold : COLORS.green;
  const priorityBg    = (p) => p==='HIGH' ? 'rgba(192,57,43,0.15)' : p==='MEDIUM' ? 'rgba(201,168,76,0.12)' : 'rgba(76,175,125,0.12)';

  const s = {
    grid:      { display:'grid', gridTemplateColumns:'1fr 300px', gap:20, marginBottom:20 },
    scoreCard: { background: COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:10, padding:20 },
    scoreRow:  { display:'flex', gap:12, marginBottom:0 },
    scoreChip: (v) => ({ flex:1, background:'#f0f4fa', border:`1px solid ${!v?COLORS.border:v>=700?COLORS.green:v>=600?COLORS.gold:'#c0392b'}`, borderRadius:8, padding:'12px', textAlign:'center' }),
    scoreNum:  (v) => ({ fontSize:'26px', fontWeight:700, color:!v?'#444':v>=700?COLORS.green:v>=600?COLORS.gold:'#c0392b' }),
    scoreBureau: { color: COLORS.muted, fontSize:'10px', marginTop:2 },
    actionCard: (sel) => ({ background: sel?'rgba(46,125,50,0.06)':'#ffffff', border:`1px solid ${sel?COLORS.green:COLORS.border}`, borderRadius:8, padding:'14px', marginBottom:10, cursor:'pointer', transition:'all 0.15s' }),
    badge: (p) => ({ display:'inline-block', padding:'2px 8px', borderRadius:3, fontSize:'10px', fontWeight:700, color: priorityColor(p), background: priorityBg(p), marginRight:8 }),
    catBadge: { display:'inline-block', padding:'2px 8px', borderRadius:3, fontSize:'10px', color: COLORS.muted, background:'#f0f4fa', border:`1px solid ${COLORS.border}` },
    actionTitle: { color: COLORS.text, fontWeight:600, fontSize:'13px', marginBottom:6, marginTop:8 },
    actionDesc: { color: COLORS.muted, fontSize:'12px', lineHeight:1.5, marginBottom:6 },
    impactChip: { display:'inline-flex', alignItems:'center', gap:4, background:'rgba(76,175,125,0.1)', border:`1px solid #2d4a1e`, borderRadius:4, padding:'3px 8px', color: COLORS.green, fontSize:'11px', fontWeight:600, marginRight:6 },
    timeChip:   { display:'inline-flex', alignItems:'center', gap:4, background:'rgba(201,168,76,0.1)', border:`1px solid #3a3010`, borderRadius:4, padding:'3px 8px', color: COLORS.gold, fontSize:'11px' },
    roundCard:  { background:'#f0f4fa', border:`1px solid ${COLORS.border}`, borderRadius:8, padding:14, marginBottom:10 },
    roundTitle: { color: COLORS.gold, fontWeight:700, fontSize:'12px', letterSpacing:'0.1em', marginBottom:6 },
    roundFocus: { color: COLORS.text, fontSize:'13px', marginBottom:8 },
    roundOutcome: { color: COLORS.green, fontSize:'11px', fontStyle:'italic' },
   projBox:    { background:'#f0f4fa', border:`1px solid ${COLORS.border}`, borderRadius:8, padding:14 },
    projLabel:  { color: COLORS.muted, fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:4 },
    projValue:  { color: COLORS.green, fontSize:'18px', fontWeight:700 },
    warningBox: { background:'rgba(201,168,76,0.08)', border:`1px solid rgba(201,168,76,0.3)`, borderRadius:8, padding:12, marginTop:12 },
    sectionTitle: { color: COLORS.gold, fontSize:'11px', letterSpacing:'0.12em', textTransform:'uppercase', fontWeight:700, marginBottom:12 },
    checkmark: (sel) => ({ width:20, height:20, borderRadius:4, border:`2px solid ${sel?COLORS.green:COLORS.border}`, background: sel?COLORS.green:'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:'#fff', fontSize:'12px', fontWeight:700 }),
    proceedBtn: { width:'100%', padding:'14px', background: COLORS.gold, border:`1px solid ${COLORS.gold}`, borderRadius:8, color:'#fff', fontSize:'14px', fontWeight:700, letterSpacing:'0.1em', cursor:'pointer', marginTop:16 },
  };

  if (loading) return (
    <div style={sharedStyles.main}>
      <div style={{ textAlign:'center', padding:'80px 0' }}>
        <div style={{ fontSize:'48px', marginBottom:16 }}>🎯</div>
        <div style={{ color: COLORS.gold, fontSize:'18px', fontWeight:600, marginBottom:8 }}>Building Your Dispute Strategy</div>
        <div style={{ color: COLORS.muted, fontSize:'14px' }}>Our team is analyzing your credit report...</div>
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:24 }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ width:10, height:10, borderRadius:'50%', background: COLORS.gold, animation:`pulse ${0.8+i*0.2}s ease-in-out infinite alternate` }} />
          ))}
        </div>
      </div>
      <style>{`@keyframes pulse { from { opacity:0.3; transform:scale(0.8); } to { opacity:1; transform:scale(1.1); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={sharedStyles.main}>
      <div style={sharedStyles.errorBox}>{error}</div>
      <button style={s.proceedBtn} onClick={generateStrategy}>Try Again</button>
    </div>
  );

  if (!strategy) return null;

  const scores = strategy && report?.scores ? report.scores : {};

  return (
    <div style={sharedStyles.main}>
      {/* TOP BAR */}
      <div style={sharedStyles.topBar}>
        <div>
          <div style={sharedStyles.pageTitle}>DISPUTE <span style={sharedStyles.pageTitleAccent}>STRATEGY</span></div>
          <div style={sharedStyles.pageSubtitle}>
            {strategy.clientName ? `Strategy for ${strategy.clientName}` : 'Your personalized credit dispute action plan'} — select the items to include in your letters.
          </div>
        </div>
        <div style={sharedStyles.memberBadge}>⭐ CFF MEMBER</div>
      </div>

      {/* SCORES */}
      <div style={{ ...sharedStyles.card, marginBottom:20 }}>
        <div style={sharedStyles.cardHeader}>
          <div style={sharedStyles.stepBadge}>📊</div>
          <div style={sharedStyles.cardTitle}>CREDIT SCORE OVERVIEW</div>
        </div>
        <div style={s.scoreRow}>
          {Object.entries(scores).map(([bureau, score]) => (
            <div key={bureau} style={s.scoreChip(score)}>
              <div style={s.scoreNum(score)}>{score || '—'}</div>
              <div style={s.scoreBureau}>{bureau}</div>
            </div>
          ))}
        </div>
        {strategy.overallScore?.summary && (
          <div style={{ color:'#888', fontSize:'12px', marginTop:12, lineHeight:1.6, padding:'10px', background:'#0f0f0f', borderRadius:6 }}>
            {strategy.overallScore.summary}
          </div>
        )}
      </div>

      <div style={s.grid}>
        {/* LEFT — ACTION ITEMS */}
        <div>
          <div style={s.sectionTitle}>
            Priority Actions — Select Items for Your Letters
            {selected.length > 0 && <span style={{ color: COLORS.green, marginLeft:8 }}>({selected.length} selected)</span>}
          </div>
          {strategy.priorityActions?.map(action => (
            <div key={action.id} style={s.actionCard(selected.includes(action.id))} onClick={() => toggleSelect(action.id)}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                <div style={s.checkmark(selected.includes(action.id))}>{selected.includes(action.id) ? '✓' : ''}</div>
                <div style={{ flex:1 }}>
                  <div>
                    <span style={s.badge(action.priority)}>{action.priority}</span>
                    <span style={s.catBadge}>{action.category}</span>
                  </div>
                  <div style={s.actionTitle}>{action.creditor} — {action.issue}</div>
                  <div style={s.actionDesc}>{action.action}</div>
                  <div>
                    {action.estimatedImpact && <span style={s.impactChip}>📈 {action.estimatedImpact}</span>}
                    {action.timeframe       && <span style={s.timeChip}>⏱ {action.timeframe}</span>}
                  </div>
                  {action.fcraGrounds && (
                    <div style={{ color:'#555', fontSize:'11px', marginTop:6 }}>⚖️ {action.fcraGrounds}</div>
                  )}
                  <div style={{ color:'#4caf7d', fontSize:'11px', marginTop:4, fontStyle:'italic' }}>Letter: {action.letterType?.replace(/-/g,' ')}</div>
                </div>
              </div>
            </div>
          ))}

          {/* QUICK WINS */}
          {strategy.quickWins?.length > 0 && (
            <div style={{ ...sharedStyles.card, marginTop:16 }}>
              <div style={s.sectionTitle}>⚡ Quick Wins</div>
              {strategy.quickWins.map((win, i) => (
                <div key={i} style={{ color: COLORS.muted, fontSize:'12px', padding:'6px 0', borderBottom:`1px solid ${COLORS.border}`, display:'flex', gap:8 }}>
                  <span style={{ color: COLORS.green }}>✓</span>{win}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — ROUND PLAN + PROJECTION */}
        <div>
          {/* ROUND PLAN */}
          <div style={{ ...sharedStyles.card, marginBottom:16 }}>
            <div style={s.sectionTitle}>📅 Dispute Round Plan</div>
            {['round1','round2','round3'].map((r, i) => {
              const rd = strategy.roundPlan?.[r];
              if (!rd) return null;
              return (
                <div key={r} style={s.roundCard}>
                  <div style={s.roundTitle}>Round {i+1}</div>
                  <div style={s.roundFocus}>{rd.focus}</div>
                  {rd.actions?.length > 0 && rd.actions.map((a, j) => (
                    <div key={j} style={{ color: COLORS.muted, fontSize:'11px', paddingLeft:8, borderLeft:`2px solid ${COLORS.border}`, marginBottom:4 }}>• {a}</div>
                  ))}
                  {rd.expectedOutcome && <div style={s.roundOutcome}>→ {rd.expectedOutcome}</div>}
                </div>
              );
            })}
          </div>

          {/* SCORE PROJECTION */}
          {strategy.scoreProjection && (
            <div style={{ ...sharedStyles.card, marginBottom:16 }}>
              <div style={s.sectionTitle}>📈 Score Projection</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                <div style={s.projBox}>
                  <div style={s.projLabel}>Current</div>
                  <div style={{ ...s.projValue, color: COLORS.muted }}>{strategy.scoreProjection.current || '—'}</div>
                </div>
                <div style={s.projBox}>
                  <div style={s.projLabel}>90 Days</div>
                  <div style={s.projValue}>{strategy.scoreProjection.projected90Days || '—'}</div>
                </div>
                <div style={s.projBox}>
                  <div style={s.projLabel}>6 Months</div>
                  <div style={{ ...s.projValue, color: COLORS.gold }}>{strategy.scoreProjection.projected6Months || '—'}</div>
                </div>
              </div>
            </div>
          )}

          {/* WARNINGS */}
          {strategy.warnings?.length > 0 && (
            <div style={s.warningBox}>
              <div style={{ color: COLORS.gold, fontWeight:600, fontSize:'12px', marginBottom:8 }}>⚠️ Important Notes</div>
              {strategy.warnings.map((w, i) => (
                <div key={i} style={{ color: COLORS.muted, fontSize:'12px', marginBottom:4 }}>• {w}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* WARNING BANNER */}
      <div style={{ background:'rgba(201,168,76,0.1)', border:`1px solid rgba(201,168,76,0.4)`, borderRadius:8, padding:'12px 16px', marginTop:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: showEmailInput ? 12 : 0 }}>
          <span style={{ fontSize:'20px' }}>⚠️</span>
          <div style={{ flex:1 }}>
            <div style={{ color: COLORS.gold, fontWeight:700, fontSize:'12px', marginBottom:3 }}>
              {emailSent ? '✅ Strategy Sent Successfully!' : 'Email Your Strategy Before Continuing!'}
            </div>
            <div style={{ color:'#888', fontSize:'11px', lineHeight:1.5 }}>
              {emailSent
                ? `Strategy report sent to ${clientEmail}. Check your inbox!`
                : 'If you close or refresh this page your strategy will be lost. Email it to yourself or your client now.'}
            </div>
          </div>
          {!emailSent && (
            <button
              onClick={() => setShowEmailInput(!showEmailInput)}
              style={{ flexShrink:0, padding:'8px 16px', background: COLORS.gold, border:`1px solid ${COLORS.gold}`, borderRadius:7, color:'#fff', fontSize:'12px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}
            >
              📧 Email Strategy
            </button>
          )}
        </div>
        {showEmailInput && !emailSent && (
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input
              type="email"
              placeholder="Enter client email address..."
              value={clientEmail}
              onChange={e => setClientEmail(e.target.value)}
              style={{ flex:1, padding:'8px 12px', background:'#f8f9fb', border:`1px solid ${COLORS.gold}`, borderRadius:6, color: COLORS.text, fontSize:'12px', outline:'none' }}
            />
            <button
              onClick={handleExport}
              disabled={emailLoading || !clientEmail.trim()}
              style={{ padding:'8px 16px', background: COLORS.green, border:'none', borderRadius:6, color:'#000', fontSize:'12px', fontWeight:700, cursor: emailLoading?'wait':'pointer', whiteSpace:'nowrap' }}
            >
              {emailLoading ? 'Sending...' : 'Send Now'}
            </button>
          </div>
        )}
        {emailError && <div style={{ color:'#e74c3c', fontSize:'11px', marginTop:6 }}>⚠️ {emailError}</div>}
      </div>

      {/* PROCEED BUTTON */}
      <button
        style={{ ...s.proceedBtn, opacity: selected.length===0?0.4:1, cursor: selected.length===0?'not-allowed':'pointer' }}
        onClick={handleProceed}
        disabled={selected.length === 0}
      >
        GENERATE DISPUTE LETTERS FOR {selected.length} SELECTED ITEM{selected.length!==1?'S':''} →
      </button>
    </div>
  );
}
